import { appColors, convertNumberToAlpha } from '@backrs_ui/common-ui';

import { Check } from '@mui/icons-material';
import { Box, SxProps, Theme, Typography } from '@mui/material';

const ANSWER_BOX_SIZE = 28;

type AnswerProps = Readonly<{
  answer: string;
  answerIndex: number;
  isSelected: boolean;
  isMultiSelect: boolean;
  selectAnswer: (answerIndex: number) => void;
}>;

export function Answer({
  answer,
  answerIndex,
  isSelected,
  isMultiSelect,
  selectAnswer,
}: AnswerProps) {
  const isSelectedBackgroundColor = isSelected ? appColors.color.blue1 : appColors.color.blue8;

  return (
    <Box
      sx={{
        ...answerContainerStyle,
        ...(isSelected && { border: `2px solid ${appColors.color.blue1}` }),
      }}
      onClick={() => selectAnswer(answerIndex)}
    >
      {/* MULTI SELECT OPTION TYPE (CHECKBOX) */}
      {isMultiSelect && (
        <Box
          sx={
            {
              ...checkBoxContainer,
              backgroundColor: isSelectedBackgroundColor,
            } as SxProps<Theme>
          }
        >
          <Check
            // if the answer isn't selected, we will still display the check mark but color it transparent. This is just to keep the sizing consistent
            style={{ color: isSelected ? appColors.neutral.white : appColors.neutral.transparent }}
          />
        </Box>
      )}

      {/* SINGLE ANSWER SELECT TYPE (A, B, C) */}
      {!isMultiSelect && (
        <Typography
          variant="h6"
          sx={{
            ...answerIndexStyle,
            backgroundColor: isSelectedBackgroundColor,
            color: isSelected ? appColors.neutral.white : appColors.color.blue1,
          }}
        >
          {convertNumberToAlpha(answerIndex)}
        </Typography>
      )}
      <Typography sx={answerTextStyle}>{answer}</Typography>
    </Box>
  );
}

const checkBoxContainer: SxProps<Theme> = {
  borderRadius: 2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginY: '0.25rem',
  width: `${ANSWER_BOX_SIZE}px`,
  minWidth: `${ANSWER_BOX_SIZE}px`,
  maxWidth: `${ANSWER_BOX_SIZE}px`,
  height: `${ANSWER_BOX_SIZE}px`,
  minHeight: `${ANSWER_BOX_SIZE}px`,
  maxHeight: `${ANSWER_BOX_SIZE}px`,
};

const answerContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '1rem',
  backgroundColor: appColors.color.blue6,
  border: `2px solid ${appColors.color.blue6}`,
  paddingLeft: '.25rem',
  paddingRight: '2rem',
  borderRadius: 3,
  cursor: 'pointer',
  userSelect: 'none',
};

const answerIndexStyle: SxProps<Theme> = {
  width: `${ANSWER_BOX_SIZE}px`,
  minWidth: `${ANSWER_BOX_SIZE}px`,
  maxWidth: `${ANSWER_BOX_SIZE}px`,
  height: `${ANSWER_BOX_SIZE - 2}px`, // we remove two px to allow padding bottom and move the letter up to center
  minHeight: `${ANSWER_BOX_SIZE - 2}px`,
  maxHeight: `${ANSWER_BOX_SIZE - 2}px`,
  pb: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 2,
  marginY: '0.25rem',
  color: appColors.neutral.white,
  fontSize: '18px',
  lineHeight: '18px',
  fontWeight: 700,
};

const answerTextStyle: SxProps<Theme> = {
  fontWeight: 700,
  color: appColors.color.blue1,
  lineHeight: '20px',
  py: 1,
};
